import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext();

// Configuration
const TOAST_CONFIG = {
  DEFAULT_DURATION: 8000,   // 8 seconds
  MAX_VISIBLE_TOASTS: 4,    // Maximum number of visible toasts
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const timersRef = useRef({});

  // Remove a toast by ID
  const removeToast = useCallback((toastId) => {
    // Clear the timer if exists
    if (timersRef.current[toastId]) {
      clearTimeout(timersRef.current[toastId]);
      delete timersRef.current[toastId];
    }

    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  }, []);

  // Add a new toast
  const addToast = useCallback((toastData) => {
    const id = ++toastIdRef.current;
    const duration = toastData.duration || TOAST_CONFIG.DEFAULT_DURATION;

    const newToast = {
      id,
      type: toastData.type || 'info',
      title: toastData.title,
      message: toastData.message,
      metadata: toastData.metadata || {},
      createdAt: new Date(),
      duration,
    };

    setToasts(prev => {
      // Keep only the most recent MAX_VISIBLE_TOASTS - 1 to make room for new one
      const existingToasts = prev.slice(0, TOAST_CONFIG.MAX_VISIBLE_TOASTS - 1);
      return [newToast, ...existingToasts];
    });

    // Set up auto-dismiss timer
    timersRef.current[id] = setTimeout(() => {
      removeToast(id);
    }, duration);

    return id;
  }, [removeToast]);

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    // Clear all timers
    Object.values(timersRef.current).forEach(timer => clearTimeout(timer));
    timersRef.current = {};
    setToasts([]);
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
